/**
 * Pulling the identifier a guided subtype actually stores out of whatever the
 * user pasted.
 *
 * Pinterest and Etsy store a bare name — `subtypeUsername`, `subtypeCompanyName`
 * — and `InputFeed::validate_subtype` interpolates it into a URL it builds
 * itself (`http://pinterest.com/{username}/feed.rss`,
 * `https://www.etsy.com/shop/{shop}/rss`). A pasted profile URL therefore lands
 * inside the prefix and produces nonsense, not an error the user can act on.
 *
 * The legacy fieldsets made that unreachable by printing the prefix beside a
 * name-only box. The redesigned fields are a plain input whose placeholder shows
 * a URL, so the copy invites exactly the value that breaks — which is what this
 * exists to absorb. Shopify and Substack need nothing here; the backend already
 * strips their URLs (`preg_replace('{^https?://([^/]+)/?.*}', ...)`).
 *
 * Nothing is normalised unless the value carries the network's OWN host. A name
 * that merely contains a slash or a dot is left exactly as typed, because
 * guessing at one of those is how a working input gets mangled.
 */

/** `pinterest.com`, `www.pinterest.com`, `pinterest.co.uk`, … */
const PINTEREST_PROFILE =
  /^(?:https?:\/\/)?(?:[\w-]+\.)*pinterest\.[a-z.]{2,}\/+([^/?#]+)/i

/**
 * Etsy shop URLs carry an optional locale segment before `shop`:
 * `etsy.com/shop/Name`, `etsy.com/uk/shop/Name`, `etsy.com/en-gb/shop/Name`.
 */
const ETSY_SHOP =
  /^(?:https?:\/\/)?(?:[\w-]+\.)*etsy\.[a-z.]{2,}\/+(?:[a-z]{2}(?:-[a-z]{2})?\/+)?shop\/+([^/?#]+)/i

/** The host on its own, for an Etsy URL that isn't a /shop/ one. */
const ETSY_HOST = /^(?:https?:\/\/)?(?:[\w-]+\.)*etsy\.[a-z.]{2,}\/+([^/?#]+)/i

function decode(segment: string): string {
  try {
    return decodeURIComponent(segment)
  } catch {
    // A lone `%` is not an escape; keep the raw text rather than throwing on it.
    return segment
  }
}

export function pinterestUsername(value: string): string {
  const trimmed = String(value ?? "").trim()
  const match = PINTEREST_PROFILE.exec(trimmed)

  return match ? decode(match[1]) : trimmed
}

export function etsyShopName(value: string): string {
  const trimmed = String(value ?? "").trim()
  const shop = ETSY_SHOP.exec(trimmed)

  if (shop) return decode(shop[1])

  /*
   * An Etsy URL that is not a shop URL: take its first segment rather than
   * leaving the host in place. `etsy.com/people/name` is a favourites profile,
   * which is a different backing field (subtypeUsername) — this at least stores
   * something a validation error can talk about instead of a whole URL.
   */
  const host = ETSY_HOST.exec(trimmed)

  return host ? decode(host[1]) : trimmed
}
